import type { ClipType, ParamKey } from './schema.ts';

export interface ParamDefinition {
  key: ParamKey;
  label: string;
  group: 'transform' | 'crop' | 'compositing' | 'audio';
  defaultValue: number;
  min?: number;
  max?: number;
  step: number;
  unit?: string;
  /** Multiplier for display (e.g. 0..1 opacity shown as 0..100 %). */
  displayScale?: number;
  appliesTo: ReadonlyArray<ClipType>;
}

const VISUAL: ReadonlyArray<ClipType> = ['video', 'image', 'text', 'solid', 'sequence'];
const AUDIBLE: ReadonlyArray<ClipType> = ['video', 'audio', 'sequence'];
const CROPPABLE: ReadonlyArray<ClipType> = ['video', 'image', 'sequence'];

/** Registry of every animatable clip parameter. UI and renderer are driven from this table. */
export const PARAM_DEFINITIONS: Readonly<Record<ParamKey, ParamDefinition>> = {
  x: {
    key: 'x',
    label: 'Position X',
    group: 'transform',
    defaultValue: 0,
    step: 1,
    unit: 'px',
    appliesTo: VISUAL,
  },
  y: {
    key: 'y',
    label: 'Position Y',
    group: 'transform',
    defaultValue: 0,
    step: 1,
    unit: 'px',
    appliesTo: VISUAL,
  },
  scale: {
    key: 'scale',
    label: 'Scale',
    group: 'transform',
    defaultValue: 1,
    min: 0,
    max: 20,
    step: 0.01,
    unit: '%',
    displayScale: 100,
    appliesTo: VISUAL,
  },
  scaleX: {
    key: 'scaleX',
    label: 'Width',
    group: 'transform',
    defaultValue: 1,
    min: 0,
    max: 20,
    step: 0.01,
    unit: '%',
    displayScale: 100,
    appliesTo: VISUAL,
  },
  scaleY: {
    key: 'scaleY',
    label: 'Height',
    group: 'transform',
    defaultValue: 1,
    min: 0,
    max: 20,
    step: 0.01,
    unit: '%',
    displayScale: 100,
    appliesTo: VISUAL,
  },
  rotation: {
    key: 'rotation',
    label: 'Rotation',
    group: 'transform',
    defaultValue: 0,
    step: 0.1,
    unit: '°',
    appliesTo: VISUAL,
  },
  opacity: {
    key: 'opacity',
    label: 'Opacity',
    group: 'compositing',
    defaultValue: 1,
    min: 0,
    max: 1,
    step: 0.01,
    unit: '%',
    displayScale: 100,
    appliesTo: VISUAL,
  },
  volume: {
    key: 'volume',
    label: 'Volume',
    group: 'audio',
    defaultValue: 1,
    min: 0,
    max: 4,
    step: 0.01,
    unit: '%',
    displayScale: 100,
    appliesTo: AUDIBLE,
  },
  cropLeft: cropDef('cropLeft', 'Crop left'),
  cropRight: cropDef('cropRight', 'Crop right'),
  cropTop: cropDef('cropTop', 'Crop top'),
  cropBottom: cropDef('cropBottom', 'Crop bottom'),
};

function cropDef(key: ParamKey, label: string): ParamDefinition {
  return {
    key,
    label,
    group: 'crop',
    defaultValue: 0,
    min: 0,
    max: 1,
    step: 0.005,
    unit: '%',
    displayScale: 100,
    appliesTo: CROPPABLE,
  };
}

export const PARAM_KEYS = Object.keys(PARAM_DEFINITIONS) as ParamKey[];

export function paramsForClipType(type: ClipType): ParamDefinition[] {
  return PARAM_KEYS.map((k) => PARAM_DEFINITIONS[k]).filter((d) => d.appliesTo.includes(type));
}

export function clampParam(key: ParamKey, value: number): number {
  const def = PARAM_DEFINITIONS[key];
  let v = value;
  if (def.min !== undefined) v = Math.max(def.min, v);
  if (def.max !== undefined) v = Math.min(def.max, v);
  return v;
}
